import moment from 'moment';
import Config from 'config';
import {v4 as uuidV4} from 'uuid';
import SearchTracking, {SEARCH_TRACKING_ACTIONS} from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import TravelDistanceAPI from 'api/travel-distance';
import {
    getTransientFacilities,
    getMonthlyFacilities,
    getTransientFacilitiesLoadMoreSpots,
    getMonthlyFacilitiesLoadMoreSpots,
    getAirportFacilities,
} from 'api/search-v2/search-api';
import {getSearchLimits} from 'utils/search-limits';
import {notify} from '../notification/notification-actions';

export const SPOTS_GET_TRANSIENT_V2 = 'SPOTS_GET_TRANSIENT_V2';
export const SPOTS_GET_MONTHLY_V2 = 'SPOTS_GET_MONTHLY_V2';
export const SPOTS_GET_AIRPORT_V2 = 'SPOTS_GET_AIRPORT_V2';
export const SPOTS_GET_TRAVEL_DISTANCES_V2 = 'SPOTS_GET_TRAVEL_DISTANCES_V2';

function handleError(dispatch, error) {
    SegmentUtils.track({
        event: 'Error Message Displayed',
        properties: {
            'error message': 'Error fetching spots',
            'error screen': 'search',
            'error action': 'fetching spots',
        },
    });

    dispatch(
        notify({
            id: uuidV4(),
            type: 'error',
            message: 'Error fetching spots',
        })
    );

    return {
        error: error?.data,
    };
}

export const loadSpotsTransientV2 = ({
    searchTrackingAction,
    locationContext,
    mapCenter,
    mapBounds,
    eventId,
}) => {
    return (dispatch, getState) => {
        const prevState = getState();
        const searchRequestState = prevState.searchRequest;
        const {
            latitude,
            longitude,
            starts,
            ends,
            operator_id: operatorId,
            rebook_reservation_id: rebookId,
        } = searchRequestState;
        const userState = prevState.user;
        const {
            data: {isAdmin},
        } = userState;
        const timezone = prevState.city.data.timezone;
        const segmentId = SegmentUtils.getAnonymousId();

        SearchTracking.updateAction(searchTrackingAction);
        const {
            sessionUUID,
            searchUUID,
            actionUUID,
            action,
        } = SearchTracking.getValues();

        const {resultLimit, maxDistance} = getSearchLimits({
            locationContext,
            mapCenter,
            mapBounds,
        });

        /* eslint-disable camelcase */
        return dispatch({
            type: SPOTS_GET_TRANSIENT_V2,
            payload: getTransientFacilities(
                {
                    ...(mapCenter
                        ? {
                              origin_lat: latitude,
                              origin_lon: longitude,
                              lat: mapCenter.latitude,
                              lon: mapCenter.longitude,
                          }
                        : {
                              lat: latitude,
                              lon: longitude,
                          }),
                    starts: moment(starts).format(
                        Config.craigApiDateTimeFormat
                    ),
                    ends: moment(ends).format(Config.craigApiDateTimeFormat),
                    show_unavailable: isAdmin,
                    page_size: resultLimit,
                    max_distance_meters: maxDistance,
                    operator_id: operatorId,
                    session_id: sessionUUID,
                    search_id: searchUUID,
                    action_id: actionUUID,
                    action,
                    fingerprint: segmentId,
                },
                {
                    eventId,
                    timezone,
                    rebookId,
                }
            ),
        }).catch(error => handleError(dispatch, error));
        /* eslint-enable camelcase */
    };
};

export const loadMoreSpotsTransientV2 = () => {
    return (dispatch, getState) => {
        const prevState = getState();
        const loadMoreSpotsUrl = prevState.spots.loadMoreSpotsUrl;

        // empty loadMoreSpotsUrl (@next) indicates no more spots are available from api
        if (!loadMoreSpotsUrl) {
            return;
        }

        SearchTracking.updateAction(SEARCH_TRACKING_ACTIONS.LIST_SEARCH_NEXT);

        return dispatch({
            type: SPOTS_GET_TRANSIENT_V2,
            payload: getTransientFacilitiesLoadMoreSpots({loadMoreSpotsUrl})
                .then(response => ({
                    loadMoreSpotsUrl: response.loadMoreSpotsUrl,
                    results: [...prevState.spots.data, ...response.results], // append to existing spots
                }))
                .catch(error => handleError(dispatch, error)),
        });
    };
};

export const loadSpotsMonthlyV2 = ({
    searchTrackingAction,
    locationContext,
    mapCenter,
    mapBounds,
}) => {
    return (dispatch, getState) => {
        const prevState = getState();
        const searchRequestState = prevState.searchRequest;
        const {
            latitude,
            longitude,
            starts,
            operator_id: operatorId,
            rebook_reservation_id: rebookId,
        } = searchRequestState;
        const userState = prevState.user;
        const {
            data: {isAdmin},
        } = userState;
        const timezone = prevState.city.data.timezone;
        const segmentId = SegmentUtils.getAnonymousId();

        SearchTracking.updateAction(searchTrackingAction);
        const {
            sessionUUID,
            searchUUID,
            actionUUID,
            action,
        } = SearchTracking.getValues();

        const {resultLimit, maxDistance} = getSearchLimits({
            locationContext,
            mapCenter,
            mapBounds,
        });

        /* eslint-disable camelcase */
        return dispatch({
            type: SPOTS_GET_MONTHLY_V2,
            payload: getMonthlyFacilities(
                {
                    ...(mapCenter
                        ? {
                              origin_lat: latitude,
                              origin_lon: longitude,
                              lat: mapCenter.latitude,
                              lon: mapCenter.longitude,
                          }
                        : {
                              lat: latitude,
                              lon: longitude,
                          }),
                    starts: moment(starts).format(
                        Config.craigApiDateTimeFormat
                    ),
                    show_unavailable: isAdmin,
                    page_size: resultLimit,
                    max_distance_meters: maxDistance,
                    operator_id: operatorId,
                    session_id: sessionUUID,
                    search_id: searchUUID,
                    action_id: actionUUID,
                    action,
                    fingerprint: segmentId,
                },
                {
                    timezone,
                    rebookId,
                }
            ),
        }).catch(error => handleError(dispatch, error));
        /* eslint-enable camelcase */
    };
};

export const loadMoreSpotsMonthlyV2 = () => {
    return (dispatch, getState) => {
        const prevState = getState();
        const loadMoreSpotsUrl = prevState.spots.loadMoreSpotsUrl;

        // empty loadMoreSpotsUrl (@next) indicates no more spots are available from api
        if (!loadMoreSpotsUrl) {
            return;
        }

        SearchTracking.updateAction(SEARCH_TRACKING_ACTIONS.LIST_SEARCH_NEXT);

        return dispatch({
            type: SPOTS_GET_MONTHLY_V2,
            payload: getMonthlyFacilitiesLoadMoreSpots({loadMoreSpotsUrl})
                .then(response => ({
                    loadMoreSpotsUrl: response.loadMoreSpotsUrl,
                    results: [...prevState.spots.data, ...response.results], // append to existing spots
                }))
                .catch(error => handleError(dispatch, error)),
        });
    };
};

export const loadSpotsAirportV2 = () => {
    return (dispatch, getState) => {
        const prevState = getState();
        const searchRequestState = prevState.searchRequest;
        const {
            starts,
            ends,
            operator_id: operatorId,
            rebook_reservation_id: rebookId,
        } = searchRequestState;
        const destination = prevState.destination.data;
        const iataCode = destination.airport.iata_code;
        const userState = prevState.user;
        const {
            data: {isAdmin},
        } = userState;
        const timezone = prevState.city.data.timezone;
        const segmentId = SegmentUtils.getAnonymousId();

        SearchTracking.updateAction(SEARCH_TRACKING_ACTIONS.SEARCH);
        const {
            sessionUUID,
            searchUUID,
            actionUUID,
            action,
        } = SearchTracking.getValues();

        /* eslint-disable camelcase */
        return dispatch({
            type: SPOTS_GET_AIRPORT_V2,
            payload: getAirportFacilities(
                {
                    iata: iataCode,
                    starts: moment(starts).format(
                        Config.craigApiDateTimeFormat
                    ),
                    ends: moment(ends).format(Config.craigApiDateTimeFormat),
                    show_unavailable: isAdmin,
                    operator_id: operatorId,
                    session_uuid: sessionUUID,
                    search_id: searchUUID,
                    action_id: actionUUID,
                    action,
                    segment_id: segmentId,
                },
                {
                    timezone,
                    rebookId,
                }
            ).then(response => {
                return {
                    results: response,
                };
            }),
        }).catch(error => handleError(dispatch, error));
        /* eslint-enable camelcase */
    };
};

export const loadTravelDistancesV2 = spotCoordinates => {
    return (dispatch, getState) => {
        const searchRequestState = getState().searchRequest;
        const destination = {
            latitude: searchRequestState.latitude,
            longitude: searchRequestState.longitude,
        };

        return dispatch({
            type: SPOTS_GET_TRAVEL_DISTANCES_V2,
            payload: TravelDistanceAPI.getTravelDistances({
                destination,
                spotCoordinates,
                isDriving: false,
            })
                .then(travelDistanceResults => {
                    return {
                        results: travelDistanceResults,
                    };
                })
                .catch(emptyResults => {
                    return {
                        results: emptyResults,
                    };
                }),
        });
    };
};

export const loadTransientSpotAndTravelDistances = ({
    searchTrackingAction,
    locationContext,
    mapCenter,
    mapBounds,
    eventId,
}) => {
    return (dispatch, getState) => {
        return dispatch(
            loadSpotsTransientV2({
                searchTrackingAction,
                locationContext,
                mapCenter,
                mapBounds,
                eventId,
            })
        ).then(() => {
            const allSpots = getState().spots.data;
            const spotCoordinates = allSpots.slice(0, 10).map(spot => {
                const primaryAddress = spot.addresses[0];

                return {
                    spotId: spot.spotId,
                    latitude: primaryAddress.latitude,
                    longitude: primaryAddress.longitude,
                };
            });

            return dispatch(loadTravelDistancesV2(spotCoordinates));
        });
    };
};

export const loadMonthlySpotAndTravelDistances = ({
    searchTrackingAction,
    locationContext,
    mapCenter,
    mapBounds,
}) => {
    return (dispatch, getState) => {
        return dispatch(
            loadSpotsMonthlyV2({
                searchTrackingAction,
                locationContext,
                mapCenter,
                mapBounds,
            })
        ).then(() => {
            const allSpots = getState().spots.data;
            const spotCoordinates = allSpots.slice(0, 10).map(spot => {
                const primaryAddress = spot.addresses[0];

                return {
                    spotId: spot.spotId,
                    latitude: primaryAddress.latitude,
                    longitude: primaryAddress.longitude,
                };
            });

            return dispatch(loadTravelDistancesV2(spotCoordinates));
        });
    };
};
